import { Group, SegmentedControl, Select, Stack, Text } from "@mantine/core";
import { useState } from "react";
import MetadataDocuments from "../MetadataDocuments";
import { ExpandGraph } from "./ExpandGraph";
import { type BucketSelection, DocumentViewMode, type DocumentViewsProps } from "./types";

const VIEW_OPTIONS = [
    { value: DocumentViewMode.TABLE, label: "Table" },
    { value: DocumentViewMode.EXPLODE, label: "Overview" },
];

export const DocumentViews = ({
    index,
    rootLabel,
    fields,
    onFilteredCountChange,
}: DocumentViewsProps) => {
    const [mode, setMode] = useState<DocumentViewMode>(DocumentViewMode.TABLE);
    const [initialFilter, setInitialFilter] = useState<BucketSelection[]>();
    const [tableKey, setTableKey] = useState(0);
    const [category, setCategory] = useState("");
    const [subCategory, setSubCategory] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | undefined>(undefined);

    const openFiltered = (selection: BucketSelection[]) => {
        setInitialFilter(selection);
        setTableKey((current) => current + 1);
        setMode(DocumentViewMode.TABLE);
    };

    const toggleExpand = (value: string) =>
        setExpanded((current) => (current === value ? undefined : value));

    const isOverview = mode === DocumentViewMode.EXPLODE;

    return (
        <Stack gap="md">
            <Group gap="lg" align="center">
                <SegmentedControl
                    data={VIEW_OPTIONS}
                    value={mode}
                    onChange={(value) => setMode(value as DocumentViewMode)}
                    w="fit-content"
                />
                {isOverview && (
                    <>
                        <Group gap="xs" wrap="nowrap">
                            <Text size="sm" fw={500}>
                                Category
                            </Text>
                            <Select
                                placeholder="Select a field"
                                data={fields}
                                value={category}
                                onChange={(value) => {
                                    setCategory(value ?? "");
                                    setExpanded(undefined);
                                }}
                                w={220}
                                searchable
                            />
                        </Group>
                        <Group gap="xs" wrap="nowrap">
                            <Text size="sm" fw={500}>
                                Sub-category
                            </Text>
                            <Select
                                placeholder="Optional — click to expand"
                                data={fields.filter((field) => field.value !== category)}
                                value={subCategory}
                                onChange={(value) => {
                                    setSubCategory(value);
                                    setExpanded(undefined);
                                }}
                                w={220}
                                searchable
                                clearable
                                disabled={!category}
                            />
                        </Group>
                    </>
                )}
            </Group>

            {isOverview ? (
                <ExpandGraph
                    index={index}
                    rootLabel={rootLabel}
                    category={category}
                    subCategory={subCategory ?? undefined}
                    expanded={expanded}
                    onToggleExpand={toggleExpand}
                    onOpen={openFiltered}
                />
            ) : (
                <MetadataDocuments
                    key={`${index}-${tableKey}`}
                    index={index}
                    initialFilter={initialFilter}
                    onFilteredCountChange={onFilteredCountChange}
                />
            )}
        </Stack>
    );
};
